// ===================================================================
// KAIXO - SupportChat
// ===================================================================
// Se renderiza ENCIMA del chat normal con la cuenta KAIXO Support.
// Muestra menú de botones (FAQs por categoría) y un input para
// preguntas libres que pasa por el matcher Claude. Si no hay match
// o el user pide humano, se abre un ticket y responde el sistema
// con "derivado, 48h".
// ===================================================================

import React, { useEffect, useState, useRef } from 'react';
import {
    supportListFaqs, supportAsk, supportCreateTicket,
    supportMarkHelpful, supportMarkEscalated, supportGetConversation
} from '../../services/supportApi';
import './Support.css';

const CATEGORY_META = {
    general:       { icon: '💬', order: 99 },
    plans:         { icon: '💰', order: 10 },
    account:       { icon: '👤', order: 20 },
    chats:         { icon: '✉️',  order: 30 },
    latidos:       { icon: '📢', order: 40 },
    direct:        { icon: '🎙️', order: 35 },
    verificacion:  { icon: '✅', order: 45 },
    latido_dorado: { icon: '💛', order: 50 },
    privacidad:    { icon: '🔒', order: 70 }
};

// i18n mínima — las cadenas de UI del propio chat
const UI = {
    español: {
        title: '¿En qué podemos ayudarte?',
        pickCategory: 'Elige una categoría o escribe tu consulta abajo.',
        back: 'Volver',
        helpful: '¿Te resolvió?',
        yes: 'Sí, gracias',
        no: 'No, quiero humano',
        categories: {
            general: 'General', plans: 'Planes y precios', account: 'Cuenta',
            chats: 'Chats y traducciones', latidos: 'Canales y latidos',
            direct: 'KAIXO Direct (cara a cara)',
            verificacion: 'Verificación de cuentas',
            latido_dorado: 'Latido Dorado', privacidad: 'Privacidad'
        },
        placeholderFree: 'Escribe tu consulta...',
        send: 'Enviar',
        thinking: 'Buscando...',
        escalateInstead: 'No encuentro una respuesta adecuada. Te derivo a nuestro equipo.',
        escalated: '✅ He derivado tu consulta a nuestro equipo. Te responderemos en máximo 48 horas.',
        askHuman: 'Hablar con un humano'
    },
    inglés: {
        title: 'How can we help?',
        pickCategory: 'Pick a category or type your question below.',
        back: 'Back',
        helpful: 'Did that help?',
        yes: 'Yes, thanks',
        no: 'No, I want a human',
        categories: {
            general: 'General', plans: 'Plans & pricing', account: 'Account',
            chats: 'Chats & translations', latidos: 'Channels & heartbeats',
            direct: 'KAIXO Direct (face-to-face)',
            verificacion: 'Account verification',
            latido_dorado: 'Golden Heartbeat', privacidad: 'Privacy'
        },
        placeholderFree: 'Type your question...',
        send: 'Send',
        thinking: 'Searching...',
        escalateInstead: 'I could not find a good answer. Let me pass this to our team.',
        escalated: '✅ Your request has been escalated. We will reply within 48 hours.',
        askHuman: 'Talk to a human'
    }
};

function pickUI(lang) { return UI[lang] || UI.inglés; }

function SupportChat({ currentUser, onBackToChat }) {
    const lang = currentUser?.language || 'español';
    const L = pickUI(lang);
    const [byCategory, setByCategory] = useState({});
    const [loadingFaqs, setLoadingFaqs] = useState(true);
    // eslint-disable-next-line no-unused-vars -- setView se usa como reset en handlers
    const [view, setView] = useState('menu'); // menu | category | answer | free
    const [activeCategory, setActiveCategory] = useState(null);
    const [activeFaq, setActiveFaq] = useState(null);
    const [freeText, setFreeText] = useState('');
    const [freeLoading, setFreeLoading] = useState(false);
    const [freeResult, setFreeResult] = useState(null); // { matched, answer, faqKey, question }
    const [escalated, setEscalated] = useState(false);
    const [conversation, setConversation] = useState([]);
    const historyRef = useRef(null);
    const [showScrollDown, setShowScrollDown] = useState(false);
    // Panel deslizable de FAQs. En móvil la conversación ocupa la mayor
    // parte de la pantalla; las categorías de FAQ se abren bajo demanda
    // con un botón fijo "Preguntas frecuentes" justo encima del input.
    const [showFaqPanel, setShowFaqPanel] = useState(false);

    // Auto-scroll al final de la historia cuando llegan mensajes nuevos
    // (sin esto, mensajes recientes quedan ocultos al final del scroll).
    useEffect(() => {
        if (historyRef.current) {
            historyRef.current.scrollTop = historyRef.current.scrollHeight;
            setShowScrollDown(false);
        }
    }, [conversation.length]);

    const scrollHistoryToBottom = () => {
        if (historyRef.current) {
            historyRef.current.scrollTo({
                top: historyRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    };

    useEffect(() => {
        let cancelled = false;
        supportListFaqs(lang)
            .then(({ data }) => { if (!cancelled) setByCategory(data.byCategory || {}); })
            .catch(() => {})
            .finally(() => { if (!cancelled) setLoadingFaqs(false); });
        // Historial de mensajes previos con Support (bienvenida + respuestas
        // humanas si las hay) — aparece arriba del panel para contexto.
        supportGetConversation()
            .then(({ data }) => { if (!cancelled) setConversation(data.messages || []); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [lang]);

    const handleAskHuman = async (sourceMessage) => {
        try {
            const msg = sourceMessage
                || (activeFaq ? `Necesito ayuda adicional sobre: ${activeFaq.question}` : 'Necesito hablar con un humano.');
            if (activeFaq) supportMarkEscalated(activeFaq.key).catch(() => {});
            await supportCreateTicket(msg, activeCategory || 'general');
            setEscalated(true);
        } catch (err) {
            console.error('[SupportChat] handleAskHuman fail:', err?.response?.data || err?.message || err);
            // Mostramos pantalla de escalado igualmente para que el user no
            // se quede sin feedback, pero logueamos para que sepamos en qué
            // paso falló (auth? red? validación?).
            setEscalated(true);
        }
    };

    const handleFreeSubmit = async () => {
        const text = freeText.trim();
        if (!text || freeLoading) return;
        setFreeLoading(true);
        setFreeResult(null);
        try {
            const { data } = await supportAsk(text);
            if (data.matched) {
                setFreeResult({
                    matched: true,
                    question: data.question,
                    answer: data.answer,
                    faqKey: data.faqKey
                });
            } else {
                // No hay match → escalar
                await supportCreateTicket(text, 'general');
                setEscalated(true);
            }
        } catch (err) {
            console.error('[SupportChat] handleFreeSubmit fail:', err?.response?.data || err?.message || err);
            // Si falla el matcher, escalamos también para no dejar al user colgado
            try {
                await supportCreateTicket(text, 'general');
                setEscalated(true);
            } catch (err2) {
                console.error('[SupportChat] fallback createTicket fail:', err2?.response?.data || err2?.message || err2);
                setEscalated(true);
            }
        } finally {
            setFreeLoading(false);
        }
    };

    // Pantalla post-escalamiento
    if (escalated) {
        return (
            <div className="sup-chat">
                <div className="sup-chat-header"><h3>KAIXO Support</h3></div>
                <div className="sup-result">
                    <div className="sup-result-icon">✅</div>
                    <p className="sup-result-msg">{L.escalated}</p>
                    <button className="sup-secondary-btn" onClick={() => {
                        setEscalated(false); setActiveFaq(null); setActiveCategory(null);
                        setFreeText(''); setFreeResult(null); setView('menu');
                        if (onBackToChat) onBackToChat();
                    }}>{L.back}</button>
                </div>
            </div>
        );
    }

    // (Las vistas de respuesta FAQ y de lista de categoría ahora se
    // renderizan DENTRO del panel deslizable — ver al final del componente.)

    // Resultado de pregunta libre con match
    if (freeResult?.matched) {
        return (
            <div className="sup-chat">
                <div className="sup-chat-header">
                    <button className="sup-back" onClick={() => setFreeResult(null)}>← {L.back}</button>
                    <h3>KAIXO Support</h3>
                </div>
                <div className="sup-answer">
                    <div className="sup-question">{freeResult.question}</div>
                    <div className="sup-answer-body">{freeResult.answer}</div>
                    <div className="sup-helpful-row">
                        <div className="sup-helpful-label">{L.helpful}</div>
                        <div className="sup-helpful-buttons">
                            <button className="sup-primary-btn" onClick={() => {
                                supportMarkHelpful(freeResult.faqKey).catch(() => {});
                                setFreeResult(null); setFreeText('');
                                if (onBackToChat) onBackToChat();
                            }}>{L.yes}</button>
                            <button className="sup-secondary-btn" onClick={() => {
                                supportMarkEscalated(freeResult.faqKey).catch(() => {});
                                handleAskHuman(freeText);
                            }}>{L.no}</button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Menú principal — nueva estructura para móvil:
    //   Header
    //   Conversación (ocupa la mayor parte del alto)
    //   Botón "Preguntas frecuentes" (fijo, abre panel)
    //   Input "Escribe tu consulta"
    //   (link humano discreto al final)
    //   Panel FAQ deslizable cuando showFaqPanel = true
    const categories = Object.keys(byCategory).sort((a, b) => {
        return (CATEGORY_META[a]?.order || 99) - (CATEGORY_META[b]?.order || 99);
    });

    const closeFaqPanel = () => {
        setShowFaqPanel(false);
        setActiveCategory(null);
        setActiveFaq(null);
    };

    return (
        <div className="sup-chat sup-chat-v2">
            <div className="sup-chat-header"><h3>KAIXO Support</h3></div>

            {/* Zona principal: conversación grande con KAIXO Support */}
            <div className="sup-conversation-area">
                {conversation.length === 0 && !loadingFaqs && (
                    <div className="sup-conversation-empty">
                        <div className="sup-conversation-empty-icon">💬</div>
                        <div className="sup-conversation-empty-title">{L.title}</div>
                        <div className="sup-conversation-empty-desc">{L.pickCategory}</div>
                    </div>
                )}
                {conversation.length > 0 && (
                    <div
                        className="sup-history sup-history-fullscreen"
                        ref={historyRef}
                        onScroll={(e) => {
                            const el = e.currentTarget;
                            const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
                            setShowScrollDown(distanceFromBottom > 80);
                        }}
                    >
                        {conversation.map(m => (
                            <div
                                key={m._id}
                                className={`sup-history-msg ${m.fromSupport ? 'from-support' : 'from-user'}`}
                            >
                                <div className="sup-history-text">{m.text}</div>
                                <div className="sup-history-time">
                                    {new Date(m.createdAt).toLocaleString()}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {showScrollDown && (
                    <button
                        type="button"
                        className="sup-scroll-down-fab"
                        onClick={scrollHistoryToBottom}
                        aria-label="Ir al último mensaje"
                    >
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="3"
                             strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 12 15 18 9" />
                            <polyline points="6 15 12 21 18 15" />
                        </svg>
                    </button>
                )}
            </div>

            {/* Botón fijo encima del input que abre el panel de FAQs */}
            <button
                type="button"
                className="sup-faq-trigger"
                onClick={() => setShowFaqPanel(true)}
            >
                📚 {L.title || 'Preguntas frecuentes'}
            </button>

            {/* Input para pregunta libre */}
            <div className="sup-free">
                <input
                    type="text"
                    value={freeText}
                    onChange={e => setFreeText(e.target.value)}
                    placeholder={L.placeholderFree}
                    onKeyDown={e => { if (e.key === 'Enter') handleFreeSubmit(); }}
                />
                <button
                    className="sup-primary-btn"
                    onClick={handleFreeSubmit}
                    disabled={!freeText.trim() || freeLoading}
                >
                    {freeLoading ? L.thinking : L.send}
                </button>
            </div>

            <button className="sup-human-link" onClick={() => handleAskHuman()}>
                {L.askHuman} →
            </button>

            {/* Panel deslizable de FAQs. Renderiza una de 3 vistas según
                estado interno: lista de categorías, FAQs de una categoría,
                o respuesta de un FAQ. Botón ✕ siempre cierra todo el panel. */}
            {showFaqPanel && (
                <div className="sup-faq-overlay" onClick={closeFaqPanel}>
                    <div className="sup-faq-panel" onClick={e => e.stopPropagation()}>
                        <div className="sup-faq-panel-header">
                            {activeFaq ? (
                                <button className="sup-back" onClick={() => setActiveFaq(null)}>← {L.back}</button>
                            ) : activeCategory ? (
                                <button className="sup-back" onClick={() => setActiveCategory(null)}>← {L.back}</button>
                            ) : (
                                <span style={{ width: 60 }} />
                            )}
                            <h3>
                                {activeFaq ? activeFaq.question
                                    : activeCategory ? (L.categories[activeCategory] || activeCategory)
                                    : (L.title || 'Preguntas frecuentes')}
                            </h3>
                            <button className="sup-faq-close" onClick={closeFaqPanel} aria-label="Cerrar">✕</button>
                        </div>

                        <div className="sup-faq-panel-body">
                            {/* Vista 3: respuesta del FAQ */}
                            {activeFaq && (
                                <div className="sup-answer">
                                    <div className="sup-answer-body">{activeFaq.answer}</div>
                                    <div className="sup-helpful-row">
                                        <div className="sup-helpful-label">{L.helpful}</div>
                                        <div className="sup-helpful-buttons">
                                            <button className="sup-primary-btn" onClick={() => {
                                                supportMarkHelpful(activeFaq.key).catch(() => {});
                                                closeFaqPanel();
                                                if (onBackToChat) onBackToChat();
                                            }}>{L.yes}</button>
                                            <button className="sup-secondary-btn" onClick={() => handleAskHuman()}>{L.no}</button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Vista 2: lista de FAQs de una categoría */}
                            {!activeFaq && activeCategory && byCategory[activeCategory] && (
                                <div className="sup-list">
                                    {byCategory[activeCategory].map(f => (
                                        <button key={f.key} className="sup-faq-item" onClick={() => setActiveFaq(f)}>
                                            <span>{f.question}</span>
                                            <span className="sup-chevron">›</span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Vista 1: grid de categorías */}
                            {!activeFaq && !activeCategory && (
                                <>
                                    {loadingFaqs && <p className="sup-dim">{L.thinking}</p>}
                                    <div className="sup-categories">
                                        {categories.map(cat => (
                                            <button key={cat} className="sup-category-btn" onClick={() => setActiveCategory(cat)}>
                                                <span className="sup-category-icon">{CATEGORY_META[cat]?.icon || '💬'}</span>
                                                <span>{L.categories[cat] || cat}</span>
                                                <span className="sup-category-count">{byCategory[cat].length}</span>
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SupportChat;
